import React, { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { ActivitiesFilter, useActivitiesContext } from 'pages/Activities/activities.context';
import FormField from 'components/Form/FormField';
import UserSelectDropdown from 'components/DropdownSelect/MultiSelectDropdown/Usage/UsersMultiSelectDropdown';
import CampaignSelectDropdown from 'components/DropdownSelect/MultiSelectDropdown/Usage/CampaignMultiSelectDropdown';
import TagsMultiSelectDropdown from 'components/DropdownSelect/MultiSelectDropdown/Usage/TagsMultiSelectDropdown';
import AnyStringSingleSelectDropdown from 'components/DropdownSelect/SingleSelectDropdown/Usage/AnyStringSingleSelectDropdown';
import CreatedBySingleSelectDropdown from 'components/DropdownSelect/SingleSelectDropdown/Usage/CreatedBySingleSelect';
import UpdatedBySingleSelectDropdown from 'components/DropdownSelect/SingleSelectDropdown/Usage/UpdatedBySingleSelect';
import Filters from 'components/Filters/Filters';
import { activityStates } from 'pages/Activities/const';
import { useAuthContext } from 'context/auth.context';
import { hasPermissions } from 'config/ability';
import StoresMultiSelectDropdown from 'components/DropdownSelect/MultiSelectDropdown/Usage/StoresMultiSelectDropdown';

const ActivityFilters = () => {
  const { activitiesFilter, setActivitiesFilter } = useActivitiesContext();
  const [filters, setFilters] = useState(activitiesFilter);
  const { ability, user, userTenantId } = useAuthContext();

  useEffect(() => {
    setFilters(activitiesFilter);
  }, [activitiesFilter]);

  const handleChangeField = (
    value: string | number | number[] | undefined,
    key: keyof ActivitiesFilter
  ) => {
    if (Array.isArray(value) && !value.length) {
      setFilters({ ...filters, [key]: [] });
    } else if (
      typeof value === 'string' &&
      ['startDate', 'dueDate', 'createdDate', 'updatedDate'].includes(key)
    ) {
      setFilters({ ...filters, [key]: DateTime.fromFormat(value, 'M/d/yyyy').toISODate() });
    } else {
      setFilters({ ...filters, [key]: value });
    }
  };

  const clearFilters = () =>
    setActivitiesFilter({
      tenantId: userTenantId,
      assignedUserIds:
        !user || hasPermissions(ability, 'read', 'Activity', { tenantId: userTenantId })
          ? []
          : [user.user.id],
    });

  const filterInputs = [
    <FormField
      field={{
        label: 'Search by Activity Name',
        value: filters.searchText,
        containedLabel: true,
        onChange: () => {},
        fieldProps: {
          size: 'small',
        },
      }}
      onChange={(searchText) => handleChangeField(searchText, 'searchText')}
      key='search'
    />,
    <UserSelectDropdown
      selectedUserIds={filters.assignedUserIds}
      containedLabel={true}
      onChange={(newUsers) =>
        handleChangeField(
          newUsers.map((user) => user.id!),
          'assignedUserIds'
        )
      }
      key='users'
      inputProps={{
        size: 'small',
      }}
    />,
    <CampaignSelectDropdown
      selectedCampaignIds={filters.campaignIds}
      containedLabel={true}
      onChange={(newCampaigns) =>
        handleChangeField(
          newCampaigns.map((campaign) => campaign.id!),
          'campaignIds'
        )
      }
      key='campaigns'
      inputProps={{
        size: 'small',
      }}
    />,
    <FormField
      field={{
        label: 'Start Date',
        value: filters.startDate,
        type: 'date',
        containedLabel: true,
        onChange: () => {},
        fieldProps: {
          size: 'small',
        },
      }}
      onChange={(startDate) => handleChangeField(startDate, 'startDate')}
      key='startDate'
    />,
    <FormField
      field={{
        label: 'Due Date',
        value: filters.dueDate,
        type: 'date',
        containedLabel: true,
        onChange: () => {},
        fieldProps: {
          size: 'small',
        },
      }}
      onChange={(dueDate) => handleChangeField(dueDate, 'dueDate')}
      key='dueDate'
    />,
    <TagsMultiSelectDropdown
      selectedTagIds={filters.tagIds}
      containedLabel={true}
      onChange={(tags) =>
        handleChangeField(
          tags.map((tag) => tag.id!),
          'tagIds'
        )
      }
      key='tags'
      inputProps={{
        size: 'small',
      }}
    />,
    <AnyStringSingleSelectDropdown
      onChange={(state) => handleChangeField(state?.name, 'status')}
      strings={activityStates}
      selectedString={filters.status}
      key='status'
      containedLabel={true}
      inputProps={{
        size: 'small',
      }}
    />,
    <StoresMultiSelectDropdown
      selectedStoreIds={filters.assignedStoreIds}
      onChange={(newStores) =>
        handleChangeField(
          newStores.map((store) => store.id!),
          'assignedStoreIds'
        )
      }
      key='stores'
      containedLabel={true}
      inputProps={{
        size: 'small',
      }}
    />,
    <CreatedBySingleSelectDropdown
      selectedUserId={filters.createdByUserId}
      onChange={(createdByUserId) => handleChangeField(createdByUserId?.id, 'createdByUserId')}
      containedLabel={true}
      key='createdByUserId'
      inputProps={{
        size: 'small',
      }}
    />,
    <UpdatedBySingleSelectDropdown
      selectedUserId={filters.updatedByUserId}
      onChange={(updatedByUserId) => handleChangeField(updatedByUserId?.id, 'updatedByUserId')}
      containedLabel={true}
      key='updatedByUserId'
      inputProps={{
        size: 'small',
      }}
    />,
    <FormField
      field={{
        label: 'Date Created',
        value: filters.createdDate,
        type: 'date',
        containedLabel: true,
        onChange: () => {},
        fieldProps: {
          size: 'small',
        },
      }}
      onChange={(createdDate) => handleChangeField(createdDate, 'createdDate')}
      key='createdDate'
    />,
    <FormField
      field={{
        label: 'Date Updated',
        value: filters.updatedDate,
        type: 'date',
        containedLabel: true,
        onChange: () => {},
        fieldProps: {
          size: 'small',
        },
      }}
      onChange={(updatedDate) => handleChangeField(updatedDate, 'updatedDate')}
      key='updatedDate'
    />,
  ];

  const hasFilters = !!Object.entries(filters).find(
    (filter) => filter[0] !== 'tenantId' && (filter[1]?.length || filter[1] > 0)
  );

  return (
    <Filters
      filterInputs={filterInputs}
      onApplyFilters={() => setActivitiesFilter(filters)}
      onClearFilters={clearFilters}
      hasFilters={hasFilters}
    />
  );
};

export default ActivityFilters;
